import React from "react";
import styled from "styled-components";
import { ResponsiveBar } from "@nivo/bar";
import { CompassParagraph } from "../common/styledComponents";
import { ElementContainer, RowContainer } from "../containers/styledComponents";
import { LegendCircle } from "../visuals/styledComponents";
import { graphPaletteV2, greyText } from "../../styles";

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const ChartContainer = styled.div<{ height: number; width: string }>`
  height: ${({ height }): number => height}px;
  width: ${({ width }): string => width};
`;

const ChartWrapper = styled.div<{ height: number }>`
  height: ${({ height }): number => height}px;
  display: flex;
  align-items: flex-start;
`;

const ScrollableLegend = styled(ElementContainer)`
  max-height: 120px;
  overflow-y: auto;
`;

// =============================================================================
// TYPES
// =============================================================================

export interface BarChartData {
  label: string;
  value: number;
}

export interface CompactBarChartProps {
  data: BarChartData[];
  height?: number;
  width?: string;
  colors?: string[];
  showLegend?: boolean;
  /** Fixed container height for consistent sizing */
  containerHeight?: number;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const CompactBarChart: React.FC<CompactBarChartProps> = ({
  data,
  height = 100,
  width = "100%",
  colors = graphPaletteV2,
  showLegend = true,
  containerHeight = 120,
}) => {
  const keys = data.map((item) => item.label);
  const chartData = [
    data.reduce(
      (acc, item) => ({
        ...acc,
        [item.label]: item.value,
      }),
      { id: "data" }
    ),
  ];

  return (
    <ChartWrapper height={containerHeight}>
      <RowContainer jc="flex-start" ai="flex-start" mt={0} mb={0}>
        <ChartContainer height={height} width={width}>
          <ResponsiveBar
            data={chartData}
            keys={keys}
            indexBy="id"
            layout="vertical"
            groupMode="stacked"
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            padding={0}
            colors={colors}
            enableGridY={false}
            enableGridX={false}
            axisTop={null}
            axisRight={null}
            axisBottom={null}
            axisLeft={null}
            enableLabel={false}
            isInteractive={true}
            borderRadius={0}
          />
        </ChartContainer>
        {showLegend && (
          <ScrollableLegend flexDirection="column" ml={12}>
            {data.map((item, index) => (
              <ElementContainer
                key={item.label}
                flexDirection="row"
                ai="center"
                mb={4}
              >
                <LegendCircle color={colors[index % colors.length]} />
                <CompassParagraph fontSize={11} ml={6} color={greyText}>
                  {item.label}: {item.value}
                </CompassParagraph>
              </ElementContainer>
            ))}
          </ScrollableLegend>
        )}
      </RowContainer>
    </ChartWrapper>
  );
};
